import React, { useState, useEffect, useRef } from "react"; // ← add useRef
import emailjs from "@emailjs/browser";

const shipmentTypes = [
  { value: "air", label: "Air Freight", icon: "✈️" },
  { value: "sea", label: "Sea Freight", icon: "🚢" },
  { value: "road", label: "Road Freight", icon: "🚛" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  shipmentType: "",
  origin: "",
  destination: "",
  weight: "",
  message: "",
};

// EmailJS Configuration - Replace with your actual IDs

const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY_Live;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID_Live;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_Live_Qoute;

export default function Quote() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  const personalInfoRef = useRef(null); // ← add this

  const validate = () => {
    const e = {};

    // Full name
    if (!form.fullName.trim()) {
      e.fullName = "Full name is required";
    } else if (form.fullName.trim().length > 50) {
      e.fullName = "Full name cannot exceed 50 characters";
    }

    // Email OR Phone — at least one required
    if (!form.email.trim() && !form.phone.trim()) {
      e.email = "Email or phone is required";
      e.phone = "Email or phone is required";
    } else {
      if (form.email.trim()) {
        if (form.email.length > 50)
          e.email = "Email cannot exceed 50 characters";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
          e.email = "Please enter a valid email address";
      }
      if (form.phone.trim()) {
        if (form.phone.length < 8)
          e.phone = "Phone number is too short (min 8 digits)";
        else if (form.phone.length > 16)
          e.phone = "Phone number is too long (max 16 digits)";
      }
    }

    // Origin
    if (form.origin.trim() && form.origin.trim().length > 50) {
      e.origin = "Origin cannot exceed 50 characters";
    }

    // Destination
    if (form.destination.trim() && form.destination.trim().length > 50) {
      e.destination = "Destination cannot exceed 50 characters";
    }

    // Weight — max 4 digits
    if (form.weight.trim()) {
      if (form.weight.length > 4) e.weight = "Weight cannot exceed 4 digits";
    }

    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const handleShipmentType = (value) => {
    setForm((prev) => ({ ...prev, shipmentType: value }));
    if (errors.shipmentType)
      setErrors((prev) => ({ ...prev, shipmentType: "" }));
    if (submitError) setSubmitError("");
  };

  const getShipmentTypeLabel = () => {
    const type = shipmentTypes.find((t) => t.value === form.shipmentType);
    return type ? type.label : "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      personalInfoRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email: "operations@load2server.com", // Change to your email
          from_name: form.fullName,
          from_email: form.email,
          phone: form.phone,
          shipment_type: getShipmentTypeLabel(),
          origin: form.origin,
          destination: form.destination,
          weight: form.weight,
          message: form.message || "No additional notes",
          reply_to: form.email,
        },
      );

      if (response.status === 200) {
        console.log("Quote request email sent successfully!");
        setSubmitted(true);
        setForm(initialForm);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setSubmitError("Failed to submit quote request. Please try again.");
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setSubmitError(
        error.text ||
          "Failed to submit quote request. Please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full form-input border rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 bg-slate-50 transition-all duration-200 focus:bg-white ${
      errors[field] ? "border-red-400 bg-red-50/30" : "border-slate-200"
    }`;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 pt-20">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-3">
            Quote Request Sent!
          </h2>
          <p className="text-slate-500 mb-8">
            Thank you, <strong>{form.fullName}</strong>! Our logistics team will
            review your shipment details and get back to you at{" "}
            <strong>{form.email}</strong> within 2 business hours.
          </p>
          <button
            onClick={() => {
              setForm(initialForm);
              setSubmitted(false);
            }}
            className="bg-[#1c7abb] hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      {/* Page header */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-blue-300 font-semibold text-sm uppercase tracking-widest">
            Quick & Easy
          </span>
          <h1 className="text-3xl sm:text-4xl font-black mt-3 mb-4">
            Request a Freight Quote
          </h1>
          <p className="text-blue-200 max-w-xl mx-auto text-base">
            Fill in your shipment details below and receive a competitive,
            tailored quote from our logistics experts within 2 hours.
          </p>
        </div>
      </div>

      {/* Form */}
      <div ref={personalInfoRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/80 border border-slate-100 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8 sm:p-12">
            {/* Error message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700 font-medium">
                  {submitError}
                </p>
              </div>
            )}

            {/* Section: Personal Info */}
            <div  className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  Personal Information
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="sm:col-span-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Full Name *
                  </label>
                  <input
                    maxLength={50}
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className={inputClass("fullName")}
                    disabled={loading}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Email Address{" "}
                    <span className="text-slate-400 font-normal normal-case">
                      (or phone)
                    </span>
                  </label>
                  <input
                    maxLength={30}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className={inputClass("email")}
                    disabled={loading}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Phone Number{" "}
                    <span className="text-slate-400 font-normal normal-case">
                      (or email)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="phone"
                    value={form.phone}
                    onChange={(e) => {
                      if (e.target.value.length <= 16) handleChange(e);
                    }}
                    placeholder="+91 98765 43210"
                    className={inputClass("phone")}
                    disabled={loading}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100 mb-10" />

            {/* Section: Shipment Type */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  Shipment Type *
                </h2>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {shipmentTypes.map((type) => (
                  <button
                    type="button"
                    key={type.value}
                    onClick={() => handleShipmentType(type.value)}
                    disabled={loading}
                    className={`flex flex-col items-center justify-center gap-2 py-5 rounded-xl border-2 transition-all duration-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                      form.shipmentType === type.value
                        ? "border-blue-600 bg-blue-50 text-blue-700 shadow-md shadow-blue-100"
                        : "border-slate-200 text-slate-500 hover:border-blue-300 hover:bg-slate-50"
                    }`}
                  >
                    <span className="text-2xl">{type.icon}</span>
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
              {errors.shipmentType && (
                <p className="text-xs text-red-500 mt-2">
                  {errors.shipmentType}
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100 mb-10" />

            {/* Section: Shipment Details */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  Shipment Details
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Origin (City / Country) *
                  </label>
                  <input
                    maxLength={20}
                    type="text"
                    name="origin"
                    value={form.origin}
                    onChange={handleChange}
                    placeholder="e.g. Toronto, Canada"
                    className={inputClass("origin")}
                    disabled={loading}
                  />
                  {errors.origin && (
                    <p className="text-xs text-red-500 mt-1">{errors.origin}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Destination (City / Country) *
                  </label>
                  <input
                    maxLength={20}
                    type="text"
                    name="destination"
                    value={form.destination}
                    onChange={handleChange}
                    placeholder="e.g. Dubai, UAE"
                    className={inputClass("destination")}
                    disabled={loading}
                  />
                  {errors.destination && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.destination}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Cargo Weight (kg) *
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={form.weight}
                    onChange={(e) => {
                      if (e.target.value.length <= 4) handleChange(e);
                    }}
                    placeholder="e.g. 5000"
                    max="9999"
                    min="1"
                    className={inputClass("weight")}
                    disabled={loading}
                  />
                  {errors.weight && (
                    <p className="text-xs text-red-500 mt-1">{errors.weight}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Additional Notes
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={1}
                    maxLength={200}
                    placeholder="Hazardous materials, temperature-sensitive, special handling..."
                    className={`${inputClass("message")} resize-none`}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1c7abb] hover:bg-blue-800 disabled:bg-blue-600 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-xl text-base transition-all duration-300 hover:shadow-xl hover:shadow-blue-700/30 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="w-5 h-5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth={4}
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Quote Request
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </>
              )}
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">
              We respect your privacy. Your information is never shared with
              third parties.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
